import { useCallback, useEffect, useState } from 'react';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum, useSubscription } from '@medplum/react';

export const usePatientEncounters = (patient?: Patient) => {
  const medplum = useMedplum();
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(false);

  const loadEncounters = useCallback(async () => {
    if (!patient?.id) {
      return;
    }

    setLoading(true);
    try {
      const result = await medplum.search(
        'Encounter',
        `patient=${patient.id}&_sort=-date`
      );
      setEncounters(result.entry?.map((e) => e.resource as Encounter) || []);
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    loadEncounters();
  }, [loadEncounters]);

  useSubscription(
    `Encounter?patient=Patient/${patient?.id}&_sort=-date`,
    (bundle) => {
      const newEncounter = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'Encounter'
      )?.resource as Encounter | undefined;

      if (!newEncounter?.id) {
        return;
      }

      setEncounters((prev) => {
        const updated = [
          newEncounter,
          ...prev.filter((e) => e.id !== newEncounter.id),
        ];

        return updated.sort((a, b) => {
          const dateA = new Date(a.period?.start ?? '').getTime();
          const dateB = new Date(b.period?.start ?? '').getTime();
          return dateB - dateA; // Descending
        });
      });
    }
  );

  return { encounters, loading, reload: loadEncounters };
};
